import React, { memo, FC, useState, useMemo } from 'react';
import parse from 'html-react-parser';
import DOMPurify from 'dompurify';
import { CardTypes } from '../utils';
import TiktokBlock from './blocks/tiktok';
import YoutubeBlock from './blocks/youtube';
import TwitterBlock from './blocks/twitter';
import BskyBlock from './blocks/bsky';
import InstagramBlock from './blocks/instagram';
import HtmlBlock from './blocks/html';
import MpxBlock from './blocks/mpx';
import DatawrapperEmbedBlock from './blocks/datawrapper-embed';
import DocumentcloudBlock from './blocks/documentcloud';
import FacebookBlock from './blocks/facebook';
import VideoPlaceholder from './video';
import VideoIframeEmbed from './videoIframe';
import DisplayCaption from './caption';
import Slideshow from '../../slideshow';
import ElectionsBlock from '../../elections-block';
import { trackClick } from '../tracking';
import {
  metaPropHandler,
  VideoContentUnitProps,
} from '../../nbc-jw-player/utils';

interface CardExpandedProps {
  card: CardTypes;
  imageUrl: string;
}

const {
  nbc: {
    brand,
    pdkAcct: accountId,
    host,
    twitterVia: twitter,
    callLetters,
    comscore_c4: station,
  },
  wp: {
    i18n: {
      __,
    },
  },
} = window;

// #region Blocks
const blockMap = {
  'core/embed/tiktok': TiktokBlock,
  'core/embed/youtube': YoutubeBlock,
  'core/embed/twitter': TwitterBlock,
  'core/embed/bluesky': BskyBlock,
  'core/embed/instagram': InstagramBlock,
  'core-embed/html': HtmlBlock,
  'core/html': DatawrapperEmbedBlock,
  'nbc/video-selector': MpxBlock,
  'core/embed/documentcloud': DocumentcloudBlock,
  'core-embed/wordpress/documentcloud': DocumentcloudBlock,
  'core/embed/facebook': FacebookBlock,
  'ninja-forms/form': HtmlBlock,
  'ninja-forms/submissions-table': HtmlBlock,
  'core/embed/datawrapper': DatawrapperEmbedBlock,
};

const noPurifyBlocks = [
  'nbc/html-block',
  'core/shortcode',
];

const renderBlock = (block) => useMemo(() => {
  const {
    blockName,
    rendered: html,
    attrs: {
      providerNameSlug,
      title: galleryTitle,
      galleryId,
      electionId,
    },
    slides,
  } = block;

  let blockType = blockName;
  if (providerNameSlug && (
    'core/embed' === blockType ||
    'core-embed/wordpress' === blockType
  )) {
    blockType = `${blockName}/${providerNameSlug}`;
  }
  const Component = blockMap[blockType];

  // #region Don't render ninja forms
  if ('ninja-forms/form' === blockName || 'ninja-forms/submissions-table' === blockName) {
    return null;
  }

  // these cannot pass through DOMPurify.
  if (- 1 !== noPurifyBlocks.indexOf(blockName)) {
    const htmlBlockHtml = parse(html);
    return htmlBlockHtml;
  }

  if ('undefined' !== typeof Component && 'nbc/gallery' !== blockName) {
    return <Component html={html} />;
  }

  if ('nbc/gallery' === blockName) {
    const prepositionText = __('of', 'nbc');

    return (
      <div className="liveblog-card__slideshow">
        <h2 className="slideshow__title">{galleryTitle}</h2>
        <Slideshow
          id={galleryId}
          title={galleryTitle}
          preposition={prepositionText}
          fullscreentext="Go Fullscreen"
          backtoarticle="Back to Card"
          isLiveBlog
          liveBlogSlides={slides}
        />
      </div>
    );
  }

  if ('nbc/election' === blockName) {
    return <ElectionsBlock electionId={electionId} />;
  }

  const sanitizedHtml = parse(DOMPurify.sanitize(html, { ADD_ATTR: ['target'] }));
  return sanitizedHtml;
}, [block]);

const CardExpanded: FC<CardExpandedProps> = ({
  card,
  card: {
    link: contentUrl,
    image: {
      alt_text: altText = '',
      caption: {
        rendered: caption = '',
      },
      media_details: {
        image_meta: {
          credit: imageMetaCredit = '',
        },
      },
    },
    is_failover_image: failoverImage,
    content_blocks: blocks,
    singular_hero: {
      type: leadType,
      url: leadIframeUrl,
      caption: videoExcerpt = '',
      credit: imageCredit,
    },
    title,
    type,
    network_object_id: networkObjectId,
    video: {
      id: vidcid,
    },
  },
  imageUrl,
}) => {
  const [showExpanded, setShowExpanded] = useState(false);
  const hasVideoLead = '' !== vidcid;
  const hasVideoIframe = 'iframe' === leadType;
  const totalBlocks = hasVideoIframe || hasVideoLead || (imageUrl && ! failoverImage) ? 4 : 5;
  const filteredCardBlocks = blocks.filter((block) => ('' !== block.rendered && '<p></p>' !== block.rendered.trim()) || 'nbc/gallery' === block.blockName);
  const expandedCardBlocks = filteredCardBlocks.slice(totalBlocks, filteredCardBlocks.length);
  const filteredPostBlocks = blocks.filter((block) => 'core/paragraph' === block.blockName && '<p></p>' !== block.rendered.trim());
  const leadImageCredit = ('' !== imageCredit) ? imageCredit : imageMetaCredit;

  const handleCardExpand = () => {
    setShowExpanded(! showExpanded);
    trackClick(`see more ${title}`, 'see more', networkObjectId, title);
  };

  return (
    <div className="liveblog-card__expanded">
      {(() => {
        if (hasVideoIframe) {
          return (
            <VideoIframeEmbed src={leadIframeUrl} title={title} />
          );
        } else if (hasVideoLead) {
          return (
            <>
              <VideoPlaceholder
                video={{
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  accountId,
                  autoplay: true,
                  brand,
                  callLetters,
                  excludeEndCard: true,
                  host,
                  station,
                  twitter,
                  videos: [{
                    ...metaPropHandler(card as VideoContentUnitProps),
                  }],
                }}
                position="lead"
              />
              {videoExcerpt &&
                <DisplayCaption text={videoExcerpt} />
              }
            </>
          );
        } else if (imageUrl && ! failoverImage) {
          return (
            <>
              <div className="liveblog-card__leadmedia lead-image">
                {leadImageCredit &&
                  <cite>{parse(DOMPurify.sanitize(leadImageCredit))}</cite>
                }
                <img
                  loading="lazy"
                  src={imageUrl}
                  alt={String(parse(DOMPurify.sanitize(altText)))}
                />
              </div>
              {caption &&
                <DisplayCaption text={caption} />
              }
            </>
          );
        }
        return '';
      })()}
      <div className="liveblog-card__body">
        {'card' === type &&
          filteredCardBlocks.slice(0, totalBlocks).map(renderBlock)
        }
        {'card' === type && 0 < expandedCardBlocks.length && showExpanded &&
          expandedCardBlocks.slice(0, expandedCardBlocks.length).map(renderBlock)
        }
        {'card' === type && 0 < expandedCardBlocks.length && ! showExpanded &&
          <button
            className="liveblog-card__cta-button"
            onClick={handleCardExpand}
            type="button"
            aria-label="expand card button"
          />
        }
        {'post' === type &&
          filteredPostBlocks.slice(0, totalBlocks).map(({ rendered: html }, index) => {
            const lastBlock = totalBlocks <= filteredPostBlocks.length ? totalBlocks : filteredPostBlocks.length;
            let modifiedHtml = html;
            if (lastBlock - 1 === index) {
              const readMoreText = __('Read Full Article', 'nbc');
              const readMoreLink = `<p><a class="liveblog-card__read-more-link" href="${contentUrl}" target="_blank">${readMoreText}</a></p>`;
              modifiedHtml += readMoreLink;
            }
            const sanitizedHtml = parse(DOMPurify.sanitize(modifiedHtml, { ADD_ATTR: ['target'] }));
            return sanitizedHtml;
          })
        }
      </div>
    </div>
  );
};

export default memo(CardExpanded);
